import { DOCUMENT, Injectable, computed, effect, inject, signal } from '@angular/core';

export type ThemeMode = 'system' | 'light' | 'dark';

/** Класс, которым PrimeNG активирует dark preset (см. darkModeSelector
 *  в main.ts и projects/widget/src/app/app.config.ts). */
const DARK_CLASS = 'p-dark';

/**
 * Глобальная тема приложения (light / dark / следовать системе).
 *
 * Хранит выбор в localStorage и вешает `.p-dark` на <html> — оттуда
 * dark-токены PrimeNG наследуют и роутовый контент, и overlay'и
 * (диалоги/меню/тосты монтируются в <body>, а не внутрь компонента,
 * поэтому класс обязан быть на корне документа).
 *
 * `suspended` нужен для `/published/**`: там страницу открывает конечный
 * пользователь виджета, и тема должна приходить из publish_options
 * агента, а не из localStorage-предпочтения администратора. Layout
 * (админский шелл) включает применение темы, публичный роут — нет.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly storageKey = 'ui_theme';

  private readonly media = this.document.defaultView?.matchMedia?.('(prefers-color-scheme: dark)') ?? null;
  private readonly systemDark = signal(this.media?.matches ?? false);
  private readonly suspended = signal(true);

  /**
   * Тема, навязанная публичной страницей агента (`publish_options.theme`).
   *
   * Перекрывает выбор пользователя и НЕ сохраняется в localStorage: это
   * не его предпочтение, а настройка владельца агента. Нужна потому, что
   * на `/published/agents/:id` документ наш целиком — фон под карточкой
   * красит `body { background-color: var(--sn-bg-ground) }`, и без класса
   * на <html> вокруг тёмной карточки оставалось белое поле.
   */
  private readonly published = signal<ThemeMode | null>(null);

  readonly mode = signal<ThemeMode>(this.read());

  /** Итоговая тема с учётом режима «как в системе». */
  readonly isDark = computed(() => {
    const mode = this.published() ?? this.mode();
    return mode === 'dark' || (mode === 'system' && this.systemDark());
  });

  constructor() {
    this.media?.addEventListener('change', (e) => this.systemDark.set(e.matches));
    effect(() => this.apply(!this.suspended() && this.isDark()));
  }

  /** Включает применение темы — зовёт админский Layout. */
  enable(): void {
    this.suspended.set(false);
  }

  /**
   * Публичная страница агента: тему диктует `publish_options`, а не
   * localStorage админа. Зовёт AgentTestComponent, когда открыт роут
   * `/published/agents/:id` — там мы владеем всем документом.
   *
   * В widget-режиме этого делать НЕЛЬЗЯ: документ принадлежит чужому
   * сайту, и трогать его <html> мы не имеем права. Поэтому там тема
   * по-прежнему живёт классом на `.tg-card` (см. cardThemeClass).
   */
  usePublishedTheme(theme: 'Light' | 'Dark' | 'Auto'): void {
    this.published.set(theme === 'Dark' ? 'dark' : theme === 'Auto' ? 'system' : 'light');
    this.suspended.set(false);
  }

  set(mode: ThemeMode): void {
    this.mode.set(mode);
    try {
      localStorage.setItem(this.storageKey, mode);
    } catch {
      // приватный режим / заблокированное хранилище — тема живёт до перезагрузки
    }
  }

  /** Переключатель light ⇄ dark: из «системной» уходим в противоположную. */
  toggle(): void {
    this.set(this.isDark() ? 'light' : 'dark');
  }

  private read(): ThemeMode {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
    } catch {
      // ignore
    }
    return 'system';
  }

  private apply(dark: boolean): void {
    const root = this.document.documentElement;
    root.classList.toggle(DARK_CLASS, dark);
    // Нативные контролы (скроллбары, date/select-попапы, autofill) —
    // без color-scheme они остаются светлыми на тёмном фоне.
    root.style.colorScheme = dark ? 'dark' : 'light';
    this.applyThemeColor(dark);
  }

  /** Цвет системной строки браузера на мобиле — иначе поверх тёмного
   *  шелла остаётся белая полоса status bar'а. */
  private applyThemeColor(dark: boolean): void {
    let meta = this.document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) {
      meta = this.document.createElement('meta');
      meta.name = 'theme-color';
      this.document.head.appendChild(meta);
    }
    meta.content = dark ? '#09090b' : '#ffffff';
  }
}
